import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { KeyboardShortcutName } from '../../../../../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { HelpModalComponent } from './help-modal.directive';

@Component({
  selector: 'h3-point-cloud-help-modal',
  templateUrl: './help-modal.directive.html',
  styleUrls: ['./help-modal.directive.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PointCloudHelpModalComponent
  extends HelpModalComponent
  implements OnInit
{
  private movementShortcutNames: KeyboardShortcutName[] = [
    'nudgeUp',
    'nudgeLeft',
    'nudgeDown',
    'nudgeRight',
    'nudgeInwards',
    'nudgeOutwards',
  ];

  private resizeShortcutNames: KeyboardShortcutName[] = [
    'contractDown',
    'contractLeft',
    'contractHeight',
    'expandRight',
    'expandUp',
    'expandHeight',
  ];

  ngOnInit() {
    this.setUpShorcuts();
  }

  setUpShorcuts() {
    this.config = {
      tabs: [
        {
          name: '3D',
          sections: [
            this.getGeneralSection(),
            this.getToolsSection(),
            this.getEditSection(),
            this.getTimelineSection(),
          ],
        },
      ],
    };
  }

  protected override getEditShortcuts() {
    return [
      ...this.getCopyShortcuts(),
      ...super.getEditShortcuts(),
      ...this.getViewSettingsShortcuts(),
    ];
  }

  protected getCopyShortcuts() {
    return [
      {
        name: 'Copy selected shapes',
        key: this.shortcut('copySelectedShapes'),
      },
      {
        name: 'Copy multiple frames',
        key: this.shortcut('copySelectedShapesAllFrames'),
      },
    ];
  }

  protected override getMovementShortcuts() {
    return [
      ...this.getMoveShortcuts(),
      ...this.getRotateShortcuts(),
      ...this.getResizeShortcuts(),
      {
        name: 'Redraw selected shape',
        key: this.shortcut('redraw'),
      },
    ];
  }

  protected getAnnotationToolsShortcuts() {
    return [
      { name: 'Select tool', key: this.shortcut('selectSelectTool') },
      { name: 'Edit tool', key: this.shortcut('selectEditTool') },
      { name: 'Cuboid tool', key: this.shortcut('selectCuboidTool') },
      { name: 'Measure tool', key: this.shortcut('selectMeasureTool') },
    ];
  }

  protected getEnhancedPointCloudShortcut() {
    return [
      {
        name: 'Enhanced point cloud',
        key: this.shortcut('toggleEnhancedPointCloud'),
      },
    ];
  }

  protected override getLayerViewSettingsShortcuts() {
    if (!this.isViewSettingsShortcutsOn) {
      return [
        { name: 'View all', key: '1' },
        { name: 'View selected', key: '2' },
        { name: 'View none', key: '3' },
      ];
    }
    return super.getLayerViewSettingsShortcuts();
  }

  protected getNavigationShortcuts() {
    return [
      { name: 'Pan', key: 'RIGHT CLICK+DRAG' },
      {
        name: 'Rotate scene',
        key: 'MIDDLE CLICK+DRAG, RIGHT CLICK+SHIFT+DRAG',
      },
      { name: 'Zoom', key: 'SCROLL' },
    ];
  }

  private getMoveShortcuts() {
    const translationShortcuts = this.movementShortcutNames.map(
      this.shortcut.bind(this),
    );

    return [
      {
        name: 'Move shape',
        key: translationShortcuts.join(', '),
      },
      {
        name: 'Move shape slowly',
        key: translationShortcuts
          .map((shortcut) => `SHIFT+${shortcut}`)
          .join(', '),
      },
    ];
  }

  private getRotateShortcuts() {
    const altName =
      this.keyboardShortcutService.humanReadableModifier('altKey');
    return [
      {
        name: 'Rotate shape',
        key: `ARROW KEYS, ${altName}+LEFT, ${altName}+RIGHT`,
      },
      {
        name: 'Rotate shape slowly',
        key: `SHIFT+ARROW KEYS, SHIFT+${altName}+LEFT, SHIFT+${altName}+RIGHT`,
      },
    ];
  }

  private getResizeShortcuts() {
    const resizeShortcuts = this.resizeShortcutNames.map(
      this.shortcut.bind(this),
    );

    return [
      {
        name: 'Resize shape',
        key: resizeShortcuts.join(', '),
      },
      {
        name: 'Resize shape slowly',
        key: resizeShortcuts.map((shortcut) => `SHIFT+${shortcut}`).join(', '),
      },
    ];
  }

  private getGeneralSection() {
    return {
      name: 'General',
      shortcuts: [
        ...this.getVisibilityShortcut(),
        ...this.getLayerViewSettingsShortcuts(),
        ...this.getInspectModeShortcut(),
        ...this.getEnhancedPointCloudShortcut(),
        ...this.getUndoRedoShortcuts(),
        ...this.getNavigationShortcuts(),
        {
          name: 'Orthographic camera',
          key: this.shortcut('toggleOrthographicCamera'),
        },
        { name: 'Reset camera', key: this.shortcut('resetSensorView') },
      ],
    };
  }

  private getToolsSection() {
    return {
      name: 'Tools',
      shortcuts: [...this.getAnnotationToolsShortcuts()],
    };
  }

  private getEditSection() {
    return {
      name: 'Edit',
      shortcuts: [...this.getEditShortcuts(), ...this.getMovementShortcuts()],
    };
  }

  private getTimelineSection() {
    return {
      name: 'Timeline',
      shortcuts: this.getScrubberShortcuts(),
    };
  }
}
